import React from 'react';
import { object, string } from 'yup';
import { Container, Box, makeStyles, Skeleton } from '@dxb-web-ui-framework/design-system';
import { FormBuilderPresenter } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/FormBuilderPresenter';
import { useTranslation } from 'react-i18next';
import { useWizardGoToNextStep } from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ErrorContent } from '../../../../content-composer/src/components/Partials/ErrorContent';
import {
  useAuthorizationErrorState,
  useAuthorizationLoadingState,
  useOnTransactionDetailsMutation,
} from '../../contexts';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(5),
    },
  },
  loaderContainer: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(3),
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
      marginTop: theme.spacing(3),
    },
  },
  externalFormBuilderElementWrapperClass: {
    margin: 'auto',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    },
  },
}));

const RejectReasonStep = () => {
  const { t } = useTranslation();
  const onGoToNextStep = useWizardGoToNextStep();

  //mutations
  const refetchTransactionDetailsMutation = useOnTransactionDetailsMutation();

  //callbacks
  const onSubmit = (formData: { cancellationReason: string }) => {
    onGoToNextStep(formData);
  };

  const classes = useStyles();

  // loading state
  const isLoadingTransactionDetails = useAuthorizationLoadingState('isLoadingTransactionDetails');
  const isLoadingCheckAuthorization = useAuthorizationLoadingState('isLoadingCheckAuthorization');

  // error state
  const transactionDetailsError = useAuthorizationErrorState('transactionDetailsError');

  const isLoading = isLoadingTransactionDetails || isLoadingCheckAuthorization;

  return (
    <Container maxWidth='sm' className={classes.container}>
      {transactionDetailsError && !isLoading && (
        <ErrorContent
          code={transactionDetailsError?.code || '404'}
          onClickBtn={refetchTransactionDetailsMutation}
          subtitle={
            transactionDetailsError?.message ||
            t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Execute.Subtitle')
          }
          btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
          message={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')}
        />
      )}
      {isLoading && (
        <Box className={classes.loaderContainer}>
          <Skeleton width='100%' variant='rect' />
          <Skeleton width='100%' variant='rect' />
        </Box>
      )}
      {!transactionDetailsError && !isLoading && (
        <FormBuilderPresenter
          submitAction={onSubmit}
          externalFormBuilderElementWrapperClass={classes.externalFormBuilderElementWrapperClass}
          formConfig={{
            validationSchema: object({
              cancellationReason: string().required(t('authorizationsListPage.ApproveRejectFlow.Validation.Reason')),
            }),
            components: [
              {
                label: t('authorizationsListPage.ApproveRejectFlow.Reason.Label'),
                key: 'cancellationReason',
                type: 'text',
                disabled: false,
              },
              {
                key: 'submitBtn',
                type: 'submit',
                properties: {
                  text: t('authorizationsListPage.ApproveRejectFlow.Submit.Text'),
                  color: 'primary',
                  variant: 'contained',
                  isDisabled: false,
                  dataTestId: 'submit',
                },
              },
            ],
          }}
        />
      )}
    </Container>
  );
};

export { RejectReasonStep };
